/* ==========================================================================
   SH · PROMO POPUP — behaviour
   Markup: sections/sh-promo-popup.liquid · CSS: assets/sh-promo-popup.css

   When it opens
     pdp  ... `data-delay` seconds after load (15 by default)
     home ... once the hero scroll sequence has finished (see watchHero)
   At most once per browser session across both pages (sessionStorage), and
   never over another open overlay — it waits for that to close instead.

   Native <dialog>: showModal() gives the top layer, focus containment and
   Escape. Every close path ends in dialog.close(); the one 'close' listener
   does the teardown (the PDP lightbox pattern, assets/sh-product.js).
   ========================================================================== */
(function () {
  'use strict';

  var html = document.documentElement;
  var KEY = 'sh_promo_seen';
  var STILL = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* Anything already holding the screen. The popup waits rather than stacking:
     a second modal over the minicart or the login panel would be hostile, and
     Escape handlers on `document` would close both at once.
     #fastrr-main-container / #headless-container are the Shiprocket checkout
     overlay (snippets/sr-checkout.liquid loads it on every page); both exist
     only while checkout is open. NOT #fastrr-prefetch-container — that one is
     always in the page. */
  var BUSY = 'dialog[open], .js-minicart.active, .hov-so[data-state="visible"],' +
             ' .sh-drawer.is-open,' +
             ' body.hov-sb-open, .hov-pre.active, #hov-chat-window.hov-chat-open,' +
             ' #fastrr-main-container, #headless-container';
  var TYPING = 'input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]),' +
               ' textarea, select, [contenteditable="true"]';

  function seen() {
    try { return window.sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }
  function markSeen() {
    try { window.sessionStorage.setItem(KEY, '1'); } catch (e) { /* storage blocked: shows once per page */ }
  }

  function busy() {
    if (document.hidden || document.querySelector(BUSY)) return true;
    var login = document.getElementById('hov-login-modal');
    if (login && login.style.display && login.style.display !== 'none') return true;
    /* Focus inside an iframe (the checkout, an embed) or a shadow root reports
       the frame / host as activeElement, never the field itself. */
    var a = document.activeElement;
    if (!a) return false;
    if (a.tagName === 'IFRAME') return true;
    if (a.shadowRoot && a.shadowRoot.activeElement) a = a.shadowRoot.activeElement;
    return !!(a.matches && a.matches(TYPING));
  }

  /* Run fn once, after the page has loaded and gone idle — or after `cap` ms,
     whichever is first. The home page's `load` can wait on 240 hero frames. */
  function afterLoad(fn, cap) {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 200);
    }
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go);
    setTimeout(go, cap);
  }

  /* Fetch what the card needs BEFORE it opens, off the critical path: the
     logo is loading="lazy" inside a closed (display:none) dialog, so it would
     otherwise only start downloading at open. The card's three Cormorant faces
     are in the global Google Fonts link, but a face no visible text uses is
     never downloaded; document.fonts.load() pulls them so nothing swaps
     mid-animation. Safe to call repeatedly (open() calls it too, in case the
     hero finished before the idle warm-up ran). */
  var fontsRequested = false;
  function warm(dlg) {
    var imgs = dlg.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) imgs[i].loading = 'eager';
    if (fontsRequested || !document.fonts || !document.fonts.load) return;
    fontsRequested = true;
    ['500 1em "Cormorant Garamond"', '600 1em "Cormorant Garamond"',
     'italic 400 1em "Cormorant Garamond"'].forEach(function (f) {
      document.fonts.load(f).catch(function () {});
    });
  }

  /* The live dialog. The theme editor re-renders the section into a NEW
     element, so the document-level listeners below look this up at event time
     instead of each init() adding its own. */
  var active = null;

  function init(dlg) {
    if (!dlg || dlg.hasAttribute('data-sh-promo-ready') || typeof dlg.showModal !== 'function') return;
    dlg.setAttribute('data-sh-promo-ready', '');

    var mode = dlg.getAttribute('data-mode');
    var design = dlg.hasAttribute('data-design');
    var delay = (parseInt(dlg.getAttribute('data-delay'), 10) || 15) * 1000;
    var codeEl = dlg.querySelector('[data-sh-promo-code]');
    var copyBtn = dlg.querySelector('[data-sh-promo-copy]');
    var copyLabel = dlg.querySelector('[data-sh-promo-copy-label]');
    var live = dlg.querySelector('[data-sh-promo-live]');
    var copyTimer = 0;

    /* Owner-set codes vary in length and letter width: shrink the code until it
       fits the pill, so it never runs under the copy glyph. Needs layout, so it
       runs once the dialog is showing, and again if the window is resized. */
    function fitCode() {
      if (!codeEl || !dlg.open) return;
      codeEl.style.fontSize = '';
      var fs = parseFloat(window.getComputedStyle(codeEl).fontSize);
      for (var i = 0; i < 16 && fs > 10 && codeEl.scrollWidth > codeEl.clientWidth; i++) {
        fs *= 0.94;
        codeEl.style.fontSize = fs + 'px';
      }
    }
    var fitTimer = 0;
    function onResize() {
      clearTimeout(fitTimer);
      fitTimer = setTimeout(fitCode, 150);
    }

    function open() {
      if (dlg.open || !dlg.isConnected) return;
      if (!design) markSeen();
      warm(dlg);
      html.classList.add('sh-promo-open');
      dlg.showModal();
      fitCode();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitCode);
      window.addEventListener('resize', onResize);
    }

    function tryOpen() {
      if (dlg.open || !dlg.isConnected || seen()) return;
      if (busy()) { setTimeout(tryOpen, 2000); return; }
      open();
    }

    active = { dlg: dlg, open: open, design: design, id: dlg.getAttribute('data-section-id') };

    /* ------------------------------------------------------- copy code */
    function resetCopy() {
      clearTimeout(copyTimer);
      if (!copyBtn) return;
      copyBtn.classList.remove('is-copied');
      if (copyLabel) copyLabel.textContent = copyLabel.getAttribute('data-label');
      if (live) live.textContent = '';
    }

    function report(ok, code) {
      clearTimeout(copyTimer);
      copyBtn.classList.toggle('is-copied', ok);
      if (copyLabel) copyLabel.textContent = ok ? 'Copied!' : 'Selected';
      if (live) live.textContent = ok ? 'Code ' + code + ' copied' : 'Code ' + code + ' selected, copy it now';
      copyTimer = setTimeout(resetCopy, 2200);
    }

    /* execCommand fallback. The textarea goes INSIDE the dialog: everything
       outside a modal dialog is inert and cannot take the selection. */
    function legacyCopy(text) {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.setAttribute('aria-hidden', 'true');
      ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
      dlg.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, text.length);
      var ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      dlg.removeChild(ta);
      try { copyBtn.focus({ preventScroll: true }); } catch (e) { /* not focusable */ }
      return ok;
    }

    function selectCode() {
      try {
        var range = document.createRange();
        range.selectNodeContents(codeEl);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      } catch (e) { /* nothing more to try */ }
    }

    function copy() {
      var text = codeEl ? codeEl.textContent.trim() : '';
      if (!text || !copyBtn) return;
      function fallback() {
        if (legacyCopy(text)) { report(true, text); return; }
        selectCode();
        report(false, text);
      }
      if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(function () { report(true, text); }, fallback);
      } else {
        fallback();
      }
    }

    /* ------------------------------------------------------- buy now */
    /* On the product page the button would link to the page the shopper is
       already on, so it closes the card and brings the pack picker into view
       instead. The <a> keeps its href, so without JS it still navigates. */
    function toPacks(form) {
      dlg.close();
      /* 'close' fires in a later task; drop the lock now or scrollTo is a no-op. */
      html.classList.remove('sh-promo-open');
      var head = document.querySelector('.sh-header-sticky');
      var offset = (head ? head.getBoundingClientRect().height : 0) + 16;
      var y = form.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: Math.max(0, y), behavior: STILL ? 'auto' : 'smooth' });
      var pick = form.querySelector('input[type="radio"]:checked') || form.querySelector('input[type="radio"]');
      if (pick) { try { pick.focus({ preventScroll: true }); } catch (e) { /* not focusable */ } }
    }

    /* ------------------------------------------------------- events */
    /* A press that starts inside the card and is released over the backdrop
       (a text drag) reports target === dialog too; only close when the press
       also STARTED on the backdrop. */
    var downOnBackdrop = false;
    dlg.addEventListener('pointerdown', function (evt) { downOnBackdrop = evt.target === dlg; });

    dlg.addEventListener('click', function (evt) {
      var t = evt.target;
      if (t === dlg) {
        if (downOnBackdrop) dlg.close();
        return;
      }
      if (!t.closest) return;
      if (t.closest('[data-sh-promo-close]')) { dlg.close(); return; }
      if (t.closest('[data-sh-promo-copy]')) { copy(); return; }
      if (mode === 'pdp' && t.closest('[data-sh-promo-buy]')) {
        var form = document.getElementById('sh-pdp-form');
        if (form) { evt.preventDefault(); toPacks(form); }
      }
    });

    dlg.addEventListener('close', function () {
      html.classList.remove('sh-promo-open');
      window.removeEventListener('resize', onResize);
      resetCopy();
    });

    /* ------------------------------------------------------- triggers */
    /* Theme editor: no timers, no session flag — selecting the section opens
       the card for editing (the listeners are at the bottom of this file). */
    if (design || seen()) return;
    afterLoad(function () { warm(dlg); }, mode === 'pdp' ? Math.max(0, delay - 6000) : 6000);

    if (mode === 'pdp') setTimeout(tryOpen, delay);
    else watchHero(tryOpen, delay);
  }

  /* "The hero scroll sequence has finished." shudhi-hero.js emits no event,
     so this measures the section itself — one rect read per scroll pause,
     never per frame.
       Scrub hero (desktop): the section (150-600svh, per its setting) is
         taller than its sticky 100svh pin; the pin releases — last frame,
         sequence done — when the section's bottom edge reaches the pin's.
       Every other mode (<=768px video hero, reduced motion, no frames)
         collapses the section to the pin's own height, so there is no
         sequence to finish: done = the hero has scrolled out of view.
     Scroll restoration and /#faq deep links are covered by the check on init. */
  function watchHero(cb, fallbackDelay) {
    var hero = document.querySelector('.sh-hero');
    var pin = hero && hero.querySelector('.sh-hero__pin');
    if (!hero || !pin) { setTimeout(cb, fallbackDelay); return; }

    var idle = 0;
    function past() {
      var r = hero.getBoundingClientRect();
      var ph = pin.getBoundingClientRect().height;
      return r.height > ph + 8 ? r.bottom <= ph + 2 : r.bottom <= 0;
    }
    function onScroll() {
      clearTimeout(idle);
      idle = setTimeout(check, 250);
    }
    function check() {
      if (!past()) return;
      window.removeEventListener('scroll', onScroll);
      /* A beat after the scroll settles; re-arm if they went back up. */
      setTimeout(function () {
        if (past()) cb();
        else window.addEventListener('scroll', onScroll, { passive: true });
      }, 500);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    check();
  }

  init(document.getElementById('sh-promo'));

  /* Back/forward cache: leaving with the card open (Buy Now on home, the Back
     button) would restore it open, over a page the shopper already acted on. */
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted && active && active.dlg.open) active.dlg.close();
  });

  /* Theme editor. A settings change re-renders the section, and a <script>
     inside re-rendered HTML does not run again — bind the new dialog here. */
  document.addEventListener('shopify:section:load', function () {
    init(document.getElementById('sh-promo'));
  });
  document.addEventListener('shopify:section:unload', function (evt) {
    if (evt.target && evt.target.querySelector && evt.target.querySelector('#sh-promo')) {
      html.classList.remove('sh-promo-open');
    }
  });
  document.addEventListener('shopify:section:select', function (evt) {
    if (active && active.design && evt.detail && evt.detail.sectionId === active.id) active.open();
  });
  document.addEventListener('shopify:section:deselect', function (evt) {
    if (active && active.design && evt.detail && evt.detail.sectionId === active.id && active.dlg.open) active.dlg.close();
  });
})();
